import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { CircularProgress, Typography, styled } from '@mui/material';
import { GridColumns, GridRowModel, GridOverlay, GridSortModel } from '@mui/x-data-grid-pro';
import { TabContainer, TabContent, TabDataGridNoRowHover } from '../../util/SharedStyles';
import NoRecordsMessage from '../NoRecordsMessage';
import { BillingAggregatesModel, ClientBillingReportType, RequestResult } from '../../gql-types.generated';
import { selectError, selectBillingAggregates, selectGetClientBillingReportStatus, captureGetClientBillingReportStatus } from '../../features/ClientDetails/ClientDetailsSlice';
import { fetchBillingAggregatesByClientKey, GetClientBillingReport } from '../../features/ClientDetails/ClientDetailsActions';
import { setToastConfig } from '../../features/EDIContainer/EDIContainerSlice';
import { ToastSeverity } from '../../util/Constants';
import { BillingReportSplitButton } from '../buttons/BillingReportSplitButton';
import { downloadDocument } from '../../util/Common';

const TabContainerWithMargin = styled(TabContainer)((props) => ({
    marginTop: '16px'
}));

interface BillingAggregatesListProps {
    internalClientCode: string;
    clientId: string;
    clientName: string;
}

const ClientBillingAggregatesList: React.FC<BillingAggregatesListProps> = props => {
    const { internalClientCode, clientId, clientName } = props;
    const dispatch = useAppDispatch();
    const [billingAggregatesRows, setBillingAggregatesRows] = useState<GridRowModel[]>([]);
    const [sortModel, setSortModel] = useState<GridSortModel>([
        {
            field: 'year',
            sort: 'desc',
        },
        {
            field: 'month',
            sort: 'desc',
        },
    ]);

    const billingAggregates = useAppSelector(selectBillingAggregates);
    const billingReportStatus = useAppSelector(selectGetClientBillingReportStatus);
    const error = useAppSelector(selectError);
    
    // Track current report request for filename generation
    const [currentReportRequest, setCurrentReportRequest] = useState<{month: number, year: number, type: string} | null>(null);

    useEffect(() => {
        if (internalClientCode) {
            dispatch(fetchBillingAggregatesByClientKey(internalClientCode));  
        }
    }, [internalClientCode]);

    useEffect(() => {
        setBillingAggregatesRows(getBillingAggregatesRows());
    }, [billingAggregates]);

    // Handle billing report status changes
    useEffect(() => {
        if (billingReportStatus?.result === RequestResult.Success && currentReportRequest) {
            if (billingReportStatus.csvBase64String) {
                const fileName = `${clientName}_${currentReportRequest.year}_${currentReportRequest.month}_${currentReportRequest.type}_BillingReport.csv`;
                
                downloadDocument(
                    billingReportStatus.csvBase64String,
                    fileName,
                    'text/csv',
                    true
                );
            }
            dispatch(setToastConfig({
                message: billingReportStatus.message || 'Report generated successfully',
                severity: ToastSeverity.Success
            }));
            resetReportDownloadStatus(); 
        } else if (billingReportStatus?.result === RequestResult.Fail) {
            dispatch(setToastConfig({
                message: billingReportStatus.message || 'Failed to generate report',
                severity: ToastSeverity.Error
            }));
            resetReportDownloadStatus(); 
        }
    }, [billingReportStatus?.result, currentReportRequest, dispatch]);

    // Action handlers for billing reports
    const handleDailyReportClick = (month: number, year: number) => {
        setCurrentReportRequest({month, year, type: 'Daily'});
        dispatch(GetClientBillingReport(clientId, month, year, ClientBillingReportType.Daily));
    };

    const handlePartnerMonthlyReportClick = (month: number, year: number) => {
        setCurrentReportRequest({month, year, type: 'PartnerMonthly'});
        dispatch(GetClientBillingReport(clientId, month, year, ClientBillingReportType.PartnerMonthly));
    };

    const resetReportDownloadStatus = () => {
        // clear current request
        setCurrentReportRequest(null);
        // remove get status
        dispatch(captureGetClientBillingReportStatus());
    };
   
    const getBillingAggregatesRows = () => {
        if (billingAggregates && billingAggregates.length > 0) {
            return billingAggregates.map((aggregatesRow: BillingAggregatesModel) => {
                const { id, year, month, currencyCode, serviceFee, documentCharges, lineCharges, totalCharges, totalDocuments, totalLines } = aggregatesRow;
                
                return {
                    _raw: aggregatesRow,
                    id,
                    year, 
                    month, 
                    currencyCode, 
                    totalDocuments, 
                    totalLines,
                    serviceFee, 
                    documentCharges, 
                    lineCharges, 
                    totalCharges, 
                } as GridRowModel;
            }) as GridRowModel[];
        } else {
            return [];
        }
    };

    const billingAggregatesColumns = useMemo<GridColumns<GridRowModel>>(
        () => [
            {
                field: 'year',
                headerName: 'YEAR',
                minWidth: 100,
                flex: 1,
                sortable: true,
            }, {
                field: 'month',
                headerName: 'MONTH',
                minWidth: 100,
                flex: 1,
                sortable: true,
            }, {
                field: 'currencyCode',
                headerName: 'CURRENCY',
                minWidth: 100,
                flex: 1,
                sortable: true,
            }, {
                field: 'totalDocuments',
                headerName: 'TOTAL DOCS',
                minWidth: 100,
                flex: 1,
                sortable: true,
            }, {
                field: 'totalLines',
                headerName: 'TOTAL LINES',
                minWidth: 100,
                flex: 1,
                sortable: true,
            }, {
                field: 'serviceFee',
                headerName: 'FEE',
                minWidth: 60,
                flex: 1,
                sortable: true,
            }, {
                field: 'documentCharges',
                headerName: 'DOC CHARGES',
                minWidth: 120,
                flex: 1,
                sortable: true,
            }, {
                field: 'lineCharges',
                headerName: 'LINE CHARGES',
                minWidth: 120,
                flex: 1,
                sortable: true,
            }, {
                field: 'totalCharges',
                headerName: 'TOTAL CHARGES',
                minWidth: 130,
                flex: 1,
                sortable: true,
            }, {
                field: 'actions',
                headerName: 'REPORTS',
                minWidth: 120,
                flex: 1,
                sortable: false,
                disableColumnMenu: true,
                renderCell: (params) => {
                    const row = params.row;
                    const month = row.month;
                    const year = row.year;
                    
                    return (
                        <BillingReportSplitButton
                            onDailyReportClick={() => handleDailyReportClick(month, year)}
                            onPartnerMonthlyReportClick={() => handlePartnerMonthlyReportClick(month, year)}
                        />
                    );
                },
            },
        ],
        [],
    );

    const loadingOverlay = () => {
        return (
            <GridOverlay>
                <CircularProgress aria-label={'progress spinner'} key={'billingAggregates-spinner'} size={42} />
            </GridOverlay>
        );
    };

    const noRowsOverlay = () => {
        return (
            <GridOverlay>
                {error && (
                    <Typography variant="body2">
                        Unable to load data. Please try again later.
                    </Typography>
                )}
                {!error && billingAggregates?.length === 0 && (
                    <NoRecordsMessage topMargin={6} message="" />
                )}
            </GridOverlay>
        );
    };

    const onSortModelChange = (model: GridSortModel) => {
        setSortModel(model);
    };


    return (
        <TabContainerWithMargin>
            <TabContent>
                <TabDataGridNoRowHover
                    loading={billingAggregates === undefined}
                    headerHeight={38}
                    rowHeight={52}
                    aria-label="Billing Aggregates List"
                    hideFooter
                    disableColumnMenu
                    disableColumnFilter
                    disableSelectionOnClick
                    rows={billingAggregatesRows}
                    columns={billingAggregatesColumns}
                    sortingOrder={['asc', 'desc']}
                    sortModel={sortModel}
                    onSortModelChange={onSortModelChange}
                    components={{
                        // eslint-disable-next-line react/display-name,@typescript-eslint/naming-convention
                        LoadingOverlay: loadingOverlay,
                        // eslint-disable-next-line react/display-name,@typescript-eslint/naming-convention
                        NoRowsOverlay: noRowsOverlay,
                    }}
                />
            </TabContent>
            {(currentReportRequest !== null) &&
                <CircularProgress
                    aria-label={'progress spinner'}
                    size={48}
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        marginTop: '-24px',
                        marginLeft: '-24px',
                        zIndex: 1500
                    }}
                />
            }
        </TabContainerWithMargin>
    );
}

export default ClientBillingAggregatesList;
